import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Lock, Loader2 } from 'lucide-react';
import { LanguageProvider, useLang } from '../contexts/LanguageContext';
import { submitContact, recordMethodView } from '../services/api';
import { LOGO_SMALL } from '../components/landing/constants';

// Unlisted method page (/method). Not in the navbar or footer by
// design: it is reached from the landing teaser or a direct link.
// It names the five moves and lists WHAT the full method contains,
// never HOW any of it works — that arrives after a request.
const MOVE_TONES = ['#00CED1', '#4ADE80', '#FFB800', '#F87171', '#A78BFA'];

const MethodBody = () => {
  const { t } = useLang('methodPage');
  const teaser = useLang('method').t;
  const [form, setForm] = useState({ name: '', email: '', company: '', role: '', message: '' });
  const [state, setState] = useState('idle');
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // Count one opening, anonymously and once per browser session.
  useEffect(() => { recordMethodView(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setState('sending');
    try {
      await submitContact({
        name: form.name,
        email: form.email,
        company: form.company,
        source: 'KANRI method request',
        message: [
          form.role && `Role: ${form.role}`,
          form.company && `Company: ${form.company}`,
          '',
          form.message || '(no detail given)',
        ].filter((l) => l !== undefined).join('\n'),
      });
      setState('done');
    } catch (_) {
      setState('error');
    }
  };

  return (
    <div className="lp-scope min-h-screen bg-[#0a0a0a] text-white">
      <header className="border-b border-white/5 px-4 sm:px-6 lg:px-8 py-5">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 text-sm text-white/60 hover:text-white transition-colors" data-testid="method-back">
            <ArrowLeft className="h-4 w-4" />
            {t.backHome}
          </Link>
          <img src={LOGO_SMALL} alt="Kaizen Kanri" className="h-8 w-8 rounded-md object-cover opacity-80" />
        </div>
      </header>

      <main className="px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-5xl mx-auto">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#00CED1]/30 bg-[#00CED1]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#00CED1]">
            <Lock className="h-3 w-3" /> {t.badge}
          </span>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl font-bold tracking-tight" data-testid="method-page-title">{t.title}</h1>
          <p className="mt-4 max-w-2xl text-lg text-white/70">{t.subtitle}</p>
          <p className="mt-3 max-w-2xl text-base text-white/45 leading-relaxed">{t.intro}</p>
          <p className="mt-6 text-xs font-semibold uppercase tracking-[0.22em] text-[#00CED1]">{teaser.tagline}</p>

          {/* Five moves — name visible, one-liner on hover only. */}
          <section className="mt-16">
            <h2 className="text-lg font-semibold">{t.movesTitle}</h2>
            <p className="mt-1.5 text-sm text-white/45">{t.movesLead}</p>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="method-page-moves">
              {(teaser.moves || []).map((m, i) => (
                <div
                  key={m.letter}
                  tabIndex={0}
                  className="group relative rounded-2xl border border-white/10 bg-white/5 p-5 min-h-[140px] flex flex-col justify-between transition-colors focus:outline-none"
                  style={{ borderTopColor: MOVE_TONES[i], borderTopWidth: 2 }}
                  data-testid={`method-page-move-${m.letter.toLowerCase()}`}
                >
                  <span className="font-display text-4xl font-bold text-white/25 transition-colors group-hover:text-white/70">
                    {m.letter}
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-[0.12em]">{m.name}</h3>
                    <p className="mt-1 text-[12px] leading-snug text-white/55 opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus:opacity-100">
                      {m.hint}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6">
              <p className="text-base text-white/80 font-medium">{teaser.pullQuote}</p>
              <p className="mt-3 text-sm text-white/45 leading-relaxed">{teaser.pullSub}</p>
            </div>
          </section>

          <section className="mt-14 grid sm:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-white/70">{t.forTitle}</h2>
              <ul className="mt-4 space-y-2.5">
                {(t.forItems || []).map((x) => (
                  <li key={x} className="flex items-start gap-2 text-sm text-white/55">
                    <Check className="mt-0.5 h-4 w-4 text-[#00CED1] shrink-0" /> {x}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-white/70">{t.scopeTitle}</h2>
              <p className="mt-4 text-sm text-white/55 leading-relaxed">{t.scopeLead}</p>
            </div>
          </section>

          {/* Locked contents — titles and one teasing line, no mechanics. */}
          <section className="mt-16">
            <h2 className="text-lg font-semibold">{t.lockedTitle}</h2>
            <p className="mt-1.5 max-w-2xl text-sm text-white/45">{t.lockedLead}</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="method-page-locked">
              {(t.locked || []).map((item) => (
                <div key={item.name} className="flex gap-3 rounded-xl border border-dashed border-white/12 bg-white/5 p-4">
                  <Lock className="mt-0.5 h-3.5 w-3.5 text-white/25 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-white/80">{item.name}</p>
                    <p className="mt-0.5 text-[12px] leading-snug text-white/40">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-16 rounded-3xl border border-[#00CED1]/25 bg-[#00CED1]/[0.04] p-8" id="request">
            <h2 className="text-2xl font-bold tracking-tight">{t.formTitle}</h2>
            <p className="mt-2 max-w-xl text-sm text-white/55">{t.formLead}</p>

            {state === 'done' ? (
              <div className="mt-8 flex items-start gap-3 rounded-2xl border border-[#4ADE80]/30 bg-[#4ADE80]/10 p-5" data-testid="method-form-success">
                <Check className="mt-0.5 h-5 w-5 text-[#4ADE80] shrink-0" />
                <div>
                  <p className="font-semibold text-white">{t.successTitle}</p>
                  <p className="mt-1 text-sm text-white/60">{t.successBody}</p>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="method-form">
                {[
                  { k: 'name', label: t.nameLabel, type: 'text', required: true },
                  { k: 'email', label: t.emailLabel, type: 'email', required: true },
                  { k: 'company', label: t.companyLabel, type: 'text', required: false },
                  { k: 'role', label: t.roleLabel, type: 'text', required: false },
                ].map((f) => (
                  <label key={f.k} className="block">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/45">{f.label}</span>
                    <input
                      type={f.type}
                      required={f.required}
                      value={form[f.k]}
                      onChange={set(f.k)}
                      className="mt-1.5 w-full rounded-xl border border-white/12 bg-white/5 px-4 py-2.5 text-sm text-white outline-none transition-colors focus:border-[#00CED1]/60"
                      data-testid={`method-form-${f.k}`}
                    />
                  </label>
                ))}
                <label className="block sm:col-span-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/45">{t.messageLabel}</span>
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={set('message')}
                    placeholder={t.messagePlaceholder}
                    className="mt-1.5 w-full rounded-xl border border-white/12 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#00CED1]/60"
                    data-testid="method-form-message"
                  />
                </label>
                <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    disabled={state === 'sending'}
                    className="inline-flex items-center gap-2 rounded-full bg-[#00CED1] px-6 py-3 text-sm font-semibold text-[#06211f] transition-all hover:brightness-110 disabled:opacity-60"
                    data-testid="method-form-submit"
                  >
                    {state === 'sending' && <Loader2 className="h-4 w-4 animate-spin" />}
                    {state === 'sending' ? t.submitting : t.submit}
                  </button>
                  <p className="text-[12px] text-white/35">
                    <span className="font-semibold uppercase tracking-[0.14em] text-white/45">{t.noteLabel}</span> · {t.note}
                  </p>
                  {state === 'error' && (
                    <p className="w-full text-sm text-[#F87171]" data-testid="method-form-error">{t.error}</p>
                  )}
                </div>
              </form>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default function MethodPage() {
  return (
    <LanguageProvider>
      <MethodBody />
    </LanguageProvider>
  );
}
